import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { toast } from "sonner";
import {
  MagnifyingGlass, ChatCircleDots, X, ArrowClockwise, CalendarBlank, Buildings, MapPin,
  Warning, ClockCounterClockwise, Envelope, DeviceMobile, Phone,
} from "@phosphor-icons/react";

/**
 * Customer Issues — list page.
 * Summary cards → filters → sortable list.
 */

const STATUSES = [
  { key: "new", label: "New", color: "#012340", bg: "#E8EDF2" },
  { key: "reviewing", label: "Reviewing", color: "#012340", bg: "#F5EBD8" },
  { key: "scheduled", label: "Scheduled", color: "#012340", bg: "#DAF3D9" },
  { key: "waiting_customer", label: "Waiting on Customer", color: "#7A5A18", bg: "#FFF2C9" },
  { key: "resolved", label: "Resolved", color: "#1B5E20", bg: "#C8E6C9" },
  { key: "closed", label: "Closed", color: "#37474F", bg: "#CFD8DC" },
];

const CATEGORIES = [
  { key: "missed_pickup", label: "Missed pickup" },
  { key: "extra_pickup", label: "Extra pickup request" },
  { key: "container_issue", label: "Container issue" },
  { key: "billing_question", label: "Billing question" },
  { key: "account_service_change", label: "Account / service change" },
  { key: "other", label: "Other" },
];

const PRIORITIES = [
  { key: "urgent", label: "Urgent", color: "#B71C1C" },
  { key: "high", label: "High", color: "#E65100" },
  { key: "normal", label: "Normal", color: "#455A64" },
  { key: "low", label: "Low", color: "#78909C" },
];

const SORT_OPTIONS = [
  { key: "newest", label: "Newest" },
  { key: "oldest", label: "Oldest" },
  { key: "priority", label: "Priority" },
  { key: "status", label: "Status" },
  { key: "last_updated", label: "Last Updated" },
];

const CONTACT_ICON = { email: Envelope, sms: DeviceMobile, phone: Phone };

const EMPTY_FILTERS = {
  q: "", status: "", category: "", priority: "", assigned_to: "",
  customer_name: "", service_location: "", date_from: "", date_to: "",
};

function formatRelative(iso) {
  if (!iso) return "—";
  const dt = new Date(iso);
  const diff = (Date.now() - dt.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return dt.toLocaleDateString();
}

function StatusPill({ status }) {
  const s = STATUSES.find((x) => x.key === status) || STATUSES[0];
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.15em]"
      style={{ background: s.bg, color: s.color }}
      data-testid={`ci-status-pill-${status}`}
    >
      {s.label}
    </span>
  );
}

function PriorityBadge({ priority }) {
  const p = PRIORITIES.find((x) => x.key === priority) || PRIORITIES[2];
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em]"
      style={{ color: p.color }}
      data-testid={`ci-priority-${priority}`}
    >
      {(priority === "urgent" || priority === "high") && <Warning size={11} weight="fill" />}
      {p.label}
    </span>
  );
}

function SummaryCard({ label, value, tone, testid }) {
  return (
    <div className="rounded-sm bg-white border border-[#012340]/10 p-4" data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C738A]">{label}</div>
      <div className="mt-1 text-3xl font-heading font-black text-[#012340]" style={tone ? { color: tone } : undefined}>{value}</div>
    </div>
  );
}

export default function CustomerIssues() {
  const [issues, setIssues] = useState([]);
  const [summary, setSummary] = useState({ counts_by_status: {}, unassigned_new: 0 });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState("newest");
  const [admins, setAdmins] = useState([]);

  async function loadSummary() {
    try {
      const { data } = await api.get("/customer-issues/summary");
      setSummary(data);
    } catch (e) { /* noop */ }
  }

  async function loadIssues() {
    setLoading(true);
    try {
      const params = { sort, limit: 200 };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/customer-issues", { params });
      setIssues(data.issues || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load issues");
    } finally { setLoading(false); }
  }

  async function loadAdmins() {
    try {
      const { data } = await api.get("/users");
      setAdmins((data.users || data || []).filter((u) => u.role === "admin"));
    } catch (e) { /* noop */ }
  }

  useEffect(() => { loadSummary(); loadAdmins(); }, []);
  useEffect(() => { loadIssues(); /* eslint-disable-line */ }, [sort, filters]);

  const setF = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const clearFilters = () => setFilters(EMPTY_FILTERS);
  const hasFilters = useMemo(() => Object.values(filters).some(Boolean), [filters]);

  return (
    <div className="px-4 md:px-8 py-6 space-y-6" data-testid="customer-issues-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#CFAB7A]">Customer Support</div>
          <h1 className="font-heading font-black text-3xl text-[#012340]">Customer Issues</h1>
          <p className="text-sm text-[#5C738A] mt-1">System of record for all customer requests submitted via the Portal or entered manually.</p>
        </div>
        <button
          onClick={() => { loadIssues(); loadSummary(); }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-white border border-[#012340]/15 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#F5F5F5]"
          data-testid="ci-refresh-btn"
        >
          <ArrowClockwise size={14} weight="bold" /> Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="ci-summary-cards">
        {STATUSES.map((s) => (
          <SummaryCard
            key={s.key}
            label={s.label}
            value={summary.counts_by_status[s.key] || 0}
            tone={s.color}
            testid={`ci-summary-${s.key}`}
          />
        ))}
      </div>

      {/* Filter bar */}
      <div className="rounded-sm bg-white border border-[#012340]/10 p-4 space-y-3" data-testid="ci-filter-bar">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C738A]" />
            <input
              value={filters.q}
              onChange={(e) => setF({ q: e.target.value })}
              placeholder="Search confirmation #, subject, customer…"
              className="w-full pl-8 pr-3 py-2 text-sm rounded-sm border border-[#012340]/20 focus:outline-none focus:border-[#CFAB7A]"
              data-testid="ci-search"
            />
          </div>
          <select value={filters.status} onChange={(e) => setF({ status: e.target.value })}
            className="px-3 py-2 text-sm rounded-sm border border-[#012340]/20 focus:outline-none focus:border-[#CFAB7A]"
            data-testid="ci-filter-status">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <select value={filters.category} onChange={(e) => setF({ category: e.target.value })}
            className="px-3 py-2 text-sm rounded-sm border border-[#012340]/20 focus:outline-none focus:border-[#CFAB7A]"
            data-testid="ci-filter-category">
            <option value="">All categories</option>
            {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <select value={filters.priority} onChange={(e) => setF({ priority: e.target.value })}
            className="px-3 py-2 text-sm rounded-sm border border-[#012340]/20 focus:outline-none focus:border-[#CFAB7A]"
            data-testid="ci-filter-priority">
            <option value="">All priorities</option>
            {PRIORITIES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
          <select value={filters.assigned_to} onChange={(e) => setF({ assigned_to: e.target.value })}
            className="px-3 py-2 text-sm rounded-sm border border-[#012340]/20 focus:outline-none focus:border-[#CFAB7A]"
            data-testid="ci-filter-assignee">
            <option value="">All assignees</option>
            <option value="unassigned">Unassigned</option>
            {admins.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <input value={filters.customer_name} onChange={(e) => setF({ customer_name: e.target.value })}
            placeholder="Customer / business…"
            className="px-3 py-2 text-sm rounded-sm border border-[#012340]/20 focus:outline-none focus:border-[#CFAB7A] flex-1 min-w-[180px]"
            data-testid="ci-filter-customer" />
          <input value={filters.service_location} onChange={(e) => setF({ service_location: e.target.value })}
            placeholder="Service location…"
            className="px-3 py-2 text-sm rounded-sm border border-[#012340]/20 focus:outline-none focus:border-[#CFAB7A] flex-1 min-w-[180px]"
            data-testid="ci-filter-location" />
          <div className="flex items-center gap-1">
            <CalendarBlank size={14} className="text-[#5C738A]" />
            <input type="date" value={filters.date_from ? filters.date_from.slice(0, 10) : ""}
              onChange={(e) => setF({ date_from: e.target.value ? `${e.target.value}T00:00:00Z` : "" })}
              className="px-2 py-2 text-sm rounded-sm border border-[#012340]/20"
              data-testid="ci-filter-date-from" />
            <span className="text-[#5C738A] text-xs">to</span>
            <input type="date" value={filters.date_to ? filters.date_to.slice(0, 10) : ""}
              onChange={(e) => setF({ date_to: e.target.value ? `${e.target.value}T23:59:59Z` : "" })}
              className="px-2 py-2 text-sm rounded-sm border border-[#012340]/20"
              data-testid="ci-filter-date-to" />
          </div>
          {hasFilters && (
            <button onClick={clearFilters}
              className="inline-flex items-center gap-1 px-3 py-2 text-xs font-bold uppercase tracking-[0.18em] text-[#E53935] hover:bg-[#FFEBEE] rounded-sm"
              data-testid="ci-clear-filters">
              <X size={12} weight="bold" /> Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Sort + list */}
      <div className="rounded-sm bg-white border border-[#012340]/10 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 bg-[#FAFAFA] border-b border-[#012340]/10">
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C738A]">
            {loading ? "Loading…" : `${issues.length} issue${issues.length === 1 ? "" : "s"}`}
          </div>
          <div className="flex items-center gap-2 text-xs text-[#5C738A]">
            <span className="font-bold uppercase tracking-[0.18em]">Sort</span>
            <select value={sort} onChange={(e) => setSort(e.target.value)}
              className="px-2 py-1 rounded-sm border border-[#012340]/20 text-xs"
              data-testid="ci-sort">
              {SORT_OPTIONS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
        </div>
        {issues.length === 0 && !loading && (
          <div className="p-10 text-center text-sm text-[#5C738A]" data-testid="ci-empty-state">
            <ChatCircleDots size={30} weight="light" className="mx-auto mb-2 text-[#012340]/30" />
            No customer issues match those filters.
          </div>
        )}
        <ul>
          {issues.map((iss) => {
            const ContactIcon = CONTACT_ICON[iss.preferred_contact] || Envelope;
            return (
              <li key={iss.id} className="border-t border-[#012340]/10 first:border-t-0" data-testid={`ci-row-${iss.id}`}>
                <Link to={`/admin/customer-issues/${iss.id}`} className="block px-4 py-3 hover:bg-[#FAFAFA]">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="font-mono text-xs font-bold text-[#012340]" data-testid={`ci-pubid-${iss.id}`}>{iss.public_id}</span>
                    <StatusPill status={iss.status} />
                    <PriorityBadge priority={iss.priority} />
                    <span className="text-xs text-[#5C738A]">{CATEGORIES.find((c) => c.key === iss.category)?.label || iss.category}</span>
                    <div className="flex-1 min-w-[200px]">
                      <div className="text-sm font-bold text-[#012340] truncate">{iss.subject}</div>
                      <div className="text-xs text-[#5C738A] flex items-center gap-2 mt-0.5 flex-wrap">
                        <Buildings size={11} weight="bold" /> <span className="truncate max-w-[240px]">{iss.customer_name}</span>
                        <MapPin size={11} weight="bold" className="ml-2" /> <span className="truncate max-w-[240px]">{iss.service_location}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C738A] flex items-center gap-1 justify-end">
                        <ContactIcon size={11} weight="bold" /> {iss.preferred_contact || "email"}
                      </div>
                      <div className="text-[10px] text-[#5C738A] flex items-center gap-1 justify-end mt-1">
                        <ClockCounterClockwise size={10} weight="bold" /> updated {formatRelative(iss.updated_at)}
                      </div>
                      {iss.assigned_to_name && (
                        <div className="text-[10px] text-[#012340] font-bold mt-1" data-testid={`ci-assignee-${iss.id}`}>
                          → {iss.assigned_to_name}
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
